/**
 * Global recovery for failed lazy-chunk loads.
 *
 * Vite emits `vite:preloadError` on `window` when a dynamic import's chunk or
 * its CSS/modulepreload dependencies fail to load (typically stale hashed
 * assets after a deploy); `event.preventDefault()` stops the error from being
 * thrown, and reloading the page is the documented remedy
 * (https://vite.dev/guide/build#load-error-handling).
 *
 * This matters here because TanStack Router's `lazyRouteComponent` (router
 * 1.139.x) keeps a failed load's error for the whole session, and only
 * reloads for "module not found" messages ("Failed to fetch dynamically
 * imported module", ...). Vite's "Unable to preload CSS" failures do not match
 * that check, so without this handler every later visit to that route throws
 * until a manual reload.
 *
 * Loop guard: a sessionStorage timestamp allows at most one automatic reload
 * per `RELOAD_COOLDOWN_MS`. If the reload did not fix it (asset genuinely
 * missing), the error is left to surface normally instead of reloading again.
 */

export const PRELOAD_RELOAD_STORAGE_KEY = 'paire:preload-error-reload-at';
export const RELOAD_COOLDOWN_MS = 30_000;

/** Returns true when the page may be reloaded now, and records the attempt. */
function claimReload(now: number): boolean {
  try {
    const last = Number(sessionStorage.getItem(PRELOAD_RELOAD_STORAGE_KEY));
    if (Number.isFinite(last) && last > 0 && now - last < RELOAD_COOLDOWN_MS) return false;
    sessionStorage.setItem(PRELOAD_RELOAD_STORAGE_KEY, String(now));
    return true;
  } catch {
    // Storage unavailable (private mode, blocked): we cannot guard against a
    // loop, so do not auto-reload.
    return false;
  }
}

/** Installs the handler once; returns an uninstall function (used by tests). */
export function installPreloadErrorReload(): () => void {
  if (typeof window === 'undefined') return () => undefined;

  const onPreloadError = (event: Event) => {
    if (!claimReload(Date.now())) return;
    event.preventDefault();
    window.location.reload();
  };

  window.addEventListener('vite:preloadError', onPreloadError);
  return () => window.removeEventListener('vite:preloadError', onPreloadError);
}
